/**
 * The editable rows grid (client, 2026-09-18: *"edit existing data"*).
 *
 * A spreadsheet over one dataset's rows: click or Enter to edit a cell, the arrows and Tab
 * to move, a change note, and Save publishes the whole edited set as a new version. The
 * state machine is `grid.ts`; this file is the surface and the two calls around it.
 *
 * **The table is windowed, not paged.** `GET /rows/all` answers the whole table because an
 * edit has to be posted against rows the client can see, and 50 000 `<tr>` elements is a
 * page that never becomes interactive. So the scroller is a fixed-height box, every row is
 * exactly `ROW_H` tall, and only the slice under the viewport is in the DOM with two spacer
 * rows standing in for the rest. Search narrows the same list, so filtering a 50 000-row
 * table is one pass and a re-slice.
 *
 * **A 422 is printed against the cell.** The server names the cell it could not coerce, and
 * a banner saying "row 12 034" over a table scrolled to row 40 is not an answer — so the
 * message is shown in the cell as well as at the foot, and the grid scrolls to it.
 *
 * **Inserts are a table-only act.** A new vector row has no geometry and the server refuses
 * it (422 `no_geometry`), so Add row is disabled on a vector with the reason said out loud
 * rather than left to be discovered at Save.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Btn } from '../../components/Btn';
import {
  ApiError,
  createDatasetVersion,
  fetchDatasetRowsAll,
  queryKeys,
  type DatasetDataType,
  type DatasetRowsAllColumn,
} from '../../lib/api';
import { toast } from '../../lib/toast';
import {
  addRow,
  applyEdit,
  cellOf,
  coerceCell,
  deleteRow,
  dirtyCount,
  initGrid,
  isDirtyRow,
  matchesQuery,
  toPayload,
  undo,
  type GridRow,
  type GridState,
} from './grid';

/** One row's height in the scroller, in pixels. The CSS rule and this constant are one fact. */
export const ROW_H = 30;
/**
 * How tall the scroller is.
 *
 * A sticky header is only worth having if the reader scrolls *inside* the box rather than
 * scrolling the box off the page, so the grid is a screenful — not the 1 200 px a
 * 40-row viewport would have been.
 */
export const SCROLLER_H = 560;
/** Roughly the header's own height, so a keyboard move does not park a row under it. */
const HEADER_H = 28;
/** How many rows are rendered at once — a viewport's worth plus a margin either side. */
export const VISIBLE_ROWS = Math.ceil(SCROLLER_H / ROW_H);
/** Rows rendered above and below the viewport so a fast scroll never shows a blank band. */
const OVERSCAN = 8;

interface CellRef {
  r: number;
  c: number;
}

interface CellError {
  message: string;
  rowId: string | null;
  rowIndex: number | null;
  key: string | null;
  /** A `conflict` carries the version somebody else published while the grid was open. */
  conflictVersion: string | null;
}

export interface RowsGridProps {
  datasetId: string;
  /** Vector or tabular — the two the server answers rows for. Inserts are tabular-only. */
  dataType: DatasetDataType;
  title?: string;
}

/**
 * What the server said, narrowed to the cell it was about.
 *
 * The 422 envelope's `details` carries `_id`, `row_index`, `column` and its own `message` —
 * the message is preferred over the envelope's summary because it is the sentence about
 * *this cell*. `row_index` is how the grid finds a row it can no longer match by id (an
 * insert the server numbered itself).
 */
function cellErrorOf(err: unknown, fallback: string): CellError {
  const empty: CellError = {
    message: fallback,
    rowId: null,
    rowIndex: null,
    key: null,
    conflictVersion: null,
  };
  if (!(err instanceof ApiError)) {
    return { ...empty, message: err instanceof Error ? err.message : fallback };
  }
  const details = (err.details && typeof err.details === 'object' ? err.details : {}) as Record<
    string,
    unknown
  >;
  const rowId = details._id ?? details.row_id ?? details.row;
  const key = details.column ?? details.key ?? details.field;
  const index = details.row_index ?? details.index;
  const current = details.current_version ?? details.version;
  return {
    message: typeof details.message === 'string' && details.message ? details.message : err.message,
    rowId: rowId === undefined || rowId === null ? null : String(rowId),
    rowIndex: typeof index === 'number' ? index : null,
    key: typeof key === 'string' ? key : null,
    conflictVersion:
      err.code === 'conflict' && (typeof current === 'string' || typeof current === 'number')
        ? String(current)
        : null,
  };
}

export function RowsGrid({ datasetId, dataType, title }: RowsGridProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scrollerRef = useRef<HTMLDivElement>(null);

  const [grid, setGrid] = useState<GridState | null>(null);
  const [q, setQ] = useState('');
  const [note, setNote] = useState('');
  const [active, setActive] = useState<CellRef | null>(null);
  const [editing, setEditing] = useState<CellRef | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<CellError | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [saving, setSaving] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.datasetRowsAll(datasetId),
    queryFn: fetchDatasetRowsAll,
    // The grid is the reader's working copy: a background refetch that replaced it would
    // throw away edits that are not saved yet.
    refetchOnWindowFocus: false,
    retry: false,
  });

  const columns: DatasetRowsAllColumn[] = useMemo(() => query.data?.columns ?? [], [query.data]);

  // The read lands once; every later change is the reader's. Keyed by the payload identity,
  // so a manual refetch after a save does replace the grid — that is a new version.
  useEffect(() => {
    if (query.data) setGrid(initGrid(query.data.rows, query.data.columns));
  }, [query.data]);

  const shown = useMemo(
    () => (grid?.rows ?? []).filter((row) => matchesQuery(row, q)),
    [grid, q],
  );
  const dirty = grid ? dirtyCount(grid) : 0;
  const canInsert = dataType === 'tabular';

  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(shown.length, start + VISIBLE_ROWS + OVERSCAN * 2);
  const slice = shown.slice(start, end);

  /** Bring a row into the scroller, and put the keyboard on one of its cells. */
  const focusCell = useCallback(
    (next: CellRef) => {
      setActive(next);
      const box = scrollerRef.current;
      if (!box) return;
      const top = next.r * ROW_H;
      // `HEADER_H`: the header is sticky, so the first `HEADER_H` pixels of the box are
      // covered — scrolling a row to `scrollTop` exactly would park it underneath.
      if (top - HEADER_H < box.scrollTop) box.scrollTop = Math.max(0, top - HEADER_H);
      else if (top + ROW_H > box.scrollTop + box.clientHeight) {
        box.scrollTop = top + ROW_H - box.clientHeight;
      }
    },
    [],
  );

  // The focus follows the active cell only while it is rendered — a cell scrolled out of the
  // window has no element to focus, and the effect re-runs when the slice brings it back.
  useEffect(() => {
    if (!active || editing) return;
    const el = scrollerRef.current?.querySelector<HTMLElement>(
      `[data-cell="${active.r}-${active.c}"]`,
    );
    if (el && document.activeElement !== el) el.focus({ preventScroll: true });
  }, [active, editing, start, end]);

  const beginEdit = useCallback(
    (ref: CellRef) => {
      const column = columns[ref.c];
      const row = shown[ref.r];
      if (!column || !row || !column.editable || row.deleted) return;
      const value = cellOf(row, column.key);
      setDraft(value === null ? '' : String(value));
      setActive(ref);
      setEditing(ref);
    },
    [columns, shown],
  );

  /** Commit the box being edited. A value the column cannot take stays in the box. */
  const commit = useCallback(
    (ref: CellRef, raw: string): boolean => {
      const column = columns[ref.c];
      const row = shown[ref.r];
      if (!column || !row) return true;
      const coerced = coerceCell(raw, column.kind);
      if (!coerced.ok) {
        setError({
          message: t(`manager.grid.bad.${coerced.error}`, { column: column.label }),
          rowId: row.id,
          rowIndex: ref.r,
          key: column.key,
          conflictVersion: null,
        });
        return false;
      }
      setError(null);
      setGrid((g) => (g ? applyEdit(g, row.id, column.key, coerced.value) : g));
      return true;
    },
    [columns, shown, t],
  );

  const move = useCallback(
    (ref: CellRef, dr: number, dc: number) => {
      let r = ref.r + dr;
      let c = ref.c + dc;
      if (c < 0) {
        c = columns.length - 1;
        r -= 1;
      } else if (c > columns.length - 1) {
        c = 0;
        r += 1;
      }
      if (r < 0 || r > shown.length - 1) return;
      focusCell({ r, c });
    },
    [columns.length, focusCell, shown.length],
  );

  const onCellKey = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>, ref: CellRef) => {
      if (e.key === 'Enter' || e.key === 'F2') {
        e.preventDefault();
        beginEdit(ref);
        return;
      }
      const deltas: Record<string, [number, number]> = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      };
      const delta = deltas[e.key];
      if (delta) {
        e.preventDefault();
        move(ref, delta[0], delta[1]);
        return;
      }
      if (e.key === 'Tab') {
        // Tab inside the grid is a cell move, not a trip through 40 000 tab stops; Shift+Tab
        // on the first cell falls through to the browser so the toolbar is still reachable.
        if (ref.r === 0 && ref.c === 0 && e.shiftKey) return;
        e.preventDefault();
        move(ref, 0, e.shiftKey ? -1 : 1);
      }
    },
    [beginEdit, move],
  );

  const onEditKey = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>, ref: CellRef) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setEditing(null);
        focusCell(ref);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!commit(ref, draft)) return;
        setEditing(null);
        if (ref.r < shown.length - 1) focusCell({ r: ref.r + 1, c: ref.c });
        else focusCell(ref);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        if (!commit(ref, draft)) return;
        setEditing(null);
        move(ref, 0, e.shiftKey ? -1 : 1);
      }
    },
    [commit, draft, focusCell, move, shown.length],
  );

  const onSave = useCallback(() => {
    if (!grid || saving || note.trim() === '') return;
    setSaving(true);
    setError(null);
    createDatasetVersion(datasetId, {
      change_note: note.trim(),
      // What the rows were read from: the server refuses the save rather than let it
      // overwrite a version published while the grid was open.
      base_version: query.data?.version ?? null,
      ...toPayload(grid, columns),
    })
      .then((created) => {
        toast(t('manager.grid.saved', { version: created.version }));
        setNote('');
        return qc.invalidateQueries({ queryKey: ['datasetRowsAll', datasetId] });
      })
      .catch((err: unknown) => {
        const shape = cellErrorOf(err, t('manager.grid.saveFailedPlain'));
        setError(shape);
        // The refused row is named, and it is very likely off screen — a message about row
        // 12 034 over a grid showing row 40 is not an answer.
        // The id is authoritative: `rowIndex` indexes the submitted payload, which drops
        // struck rows and ignores the search filter, so it can point one row off.
        const byId = shape.rowId === null ? -1 : shown.findIndex((row) => row.id === shape.rowId);
        const index = byId >= 0 ? byId : (shape.rowIndex ?? -1);
        if (index >= 0 && index < shown.length) {
          const c = Math.max(0, columns.findIndex((col) => col.key === shape.key));
          focusCell({ r: index, c });
        }
        toast(
          shape.conflictVersion
            ? t('manager.grid.conflictToast', { version: shape.conflictVersion })
            : t('manager.grid.saveFailed', { message: shape.message }),
        );
      })
      .finally(() => setSaving(false));
  }, [columns, datasetId, focusCell, grid, note, qc, query.data, saving, shown, t]);

  /** Throw the working copy away and re-read — the only honest answer to a conflict. */
  const onReload = useCallback(() => {
    setError(null);
    setGrid(null);
    setNote('');
    void qc.invalidateQueries({ queryKey: ['datasetRowsAll', datasetId] });
  }, [datasetId, qc]);

  if (query.isPending) {
    return <p className="rows-note">{t('manager.grid.loading')}</p>;
  }
  if (query.isError) {
    const status = query.error instanceof ApiError ? query.error.status : 0;
    return (
      <p className="rows-note" data-testid="mgr-grid-error">
        {status === 404 ? t('manager.grid.noRows') : t('manager.grid.loadFailed')}
      </p>
    );
  }

  const capped = (query.data?.count ?? 0) >= (query.data?.cap ?? Infinity);

  return (
    <section className="mgr-grid" data-testid="mgr-grid" aria-label={title ?? datasetId}>
      {/* Which version is on the table. The save publishes the next one, so the reader is
          owed the number they are editing *from* — and it is what a conflict is about. */}
      <p className="mgr-grid-version" data-testid="mgr-grid-version">
        {query.data?.version
          ? t('manager.grid.editingVersion', { version: query.data.version })
          : t('manager.grid.editingUnversioned')}
      </p>
      <div className="mgr-grid-bar">
        <label className="mgr-grid-search" htmlFor="mgr-grid-search">
          <span className="mgr-label">{t('manager.grid.search')}</span>
          <input
            id="mgr-grid-search"
            data-testid="mgr-grid-search"
            className="mgr-input"
            type="search"
            value={q}
            placeholder={t('manager.grid.searchPlaceholder')}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(null);
              setEditing(null);
            }}
          />
        </label>
        <div className="mgr-grid-acts">
          <Btn
            size="sm"
            icon="plus"
            data-testid="mgr-grid-add"
            disabled={!canInsert}
            title={canInsert ? undefined : t('manager.grid.noInsert')}
            onClick={() => grid && setGrid(addRow(grid, columns))}
          >
            {t('manager.grid.add')}
          </Btn>
          <Btn
            size="sm"
            icon="sync"
            data-testid="mgr-grid-undo"
            disabled={!grid || grid.log.length === 0}
            onClick={() => grid && setGrid(undo(grid))}
          >
            {t('manager.grid.undo')}
          </Btn>
          <span className="mgr-grid-count" data-testid="mgr-grid-dirty">
            {t('manager.grid.dirty', { n: dirty })}
          </span>
        </div>
      </div>

      {canInsert ? null : (
        <p className="mgr-hint" data-testid="mgr-grid-insert-hint">
          {t('manager.grid.noInsert')}
        </p>
      )}
      {capped ? (
        <p className="mgr-hint" data-testid="mgr-grid-cap">
          {t('manager.grid.capped', { cap: query.data?.cap ?? 0 })}
        </p>
      ) : null}

      <div
        className="mgr-grid-scroll"
        ref={scrollerRef}
        style={{ maxHeight: `${SCROLLER_H}px` }}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        <table className="mgr-grid-table">
          <thead>
            <tr>
              <th scope="col" className="mgr-grid-gutter">
                {t('manager.grid.rowCol')}
              </th>
              {columns.map((col) => (
                <th key={col.key} scope="col" className={col.editable ? '' : 'ro'}>
                  {col.label}
                </th>
              ))}
              <th scope="col" className="mgr-grid-gutter">
                {t('manager.grid.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* One cell spanning the table, not an empty `<tr>`: a row with no cells is a
                row the browser may collapse, and the window would then sit at the wrong
                scroll offset. `columns.length + 2` is the gutter either side. */}
            {start > 0 ? (
              <tr aria-hidden="true" data-testid="mgr-grid-spacer-top">
                <td colSpan={columns.length + 2} style={{ height: `${start * ROW_H}px`, padding: 0 }} />
              </tr>
            ) : null}
            {slice.map((row, i) => {
              const r = start + i;
              return (
                <GridTr
                  key={row.id}
                  row={row}
                  r={r}
                  columns={columns}
                  dirty={grid ? isDirtyRow(grid, row) : false}
                  active={active}
                  firstStop={active === null || active.r < start || active.r >= end ? start : null}
                  editing={editing}
                  draft={draft}
                  error={error}
                  onDraft={setDraft}
                  onActivate={beginEdit}
                  onCellKey={onCellKey}
                  onEditKey={onEditKey}
                  onBlurCommit={(ref) => {
                    // The box closes either way: a refusal leaves the cell as it was, with
                    // the reason at the foot. A box that would not close on blur is a trap.
                    commit(ref, draft);
                    setEditing(null);
                  }}
                  onDelete={() => grid && setGrid(deleteRow(grid, row.id, !row.deleted))}
                />
              );
            })}
            {end < shown.length ? (
              <tr aria-hidden="true" data-testid="mgr-grid-spacer-bottom">
                <td
                  colSpan={columns.length + 2}
                  style={{ height: `${(shown.length - end) * ROW_H}px`, padding: 0 }}
                />
              </tr>
            ) : null}
          </tbody>
        </table>
        {shown.length === 0 ? (
          <p className="rows-note" data-testid="mgr-grid-empty">
            {t('manager.grid.empty')}
          </p>
        ) : null}
      </div>

      {error ? (
        <p className="mgr-error mgr-grid-errbar" role="alert" data-testid="mgr-grid-err">
          <span>
            {error.conflictVersion
              ? t('manager.grid.conflict', { version: error.conflictVersion })
              : error.message}
          </span>
          {error.conflictVersion ? (
            <Btn size="sm" icon="sync" data-testid="mgr-grid-reload" onClick={onReload}>
              {t('manager.grid.reload')}
            </Btn>
          ) : null}
        </p>
      ) : null}

      <footer className="mgr-grid-foot">
        <label className="mgr-field mgr-grid-notefield" htmlFor="mgr-grid-note">
          <span className="mgr-label">
            {t('manager.grid.note')} <b aria-hidden="true">*</b>
          </span>
          <input
            id="mgr-grid-note"
            data-testid="mgr-grid-note"
            className="mgr-input"
            required
            value={note}
            placeholder={t('manager.grid.notePlaceholder')}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <Btn
          variant="primary"
          data-testid="mgr-grid-save"
          disabled={saving || note.trim() === '' || dirty === 0}
          onClick={onSave}
        >
          {saving ? t('manager.grid.saving') : t('manager.grid.save')}
        </Btn>
      </footer>
    </section>
  );
}

interface GridTrProps {
  row: GridRow;
  r: number;
  columns: DatasetRowsAllColumn[];
  dirty: boolean;
  active: CellRef | null;
  /** The row index that holds the grid's single tab stop while the active cell is off-slice. */
  firstStop: number | null;
  editing: CellRef | null;
  draft: string;
  error: CellError | null;
  onDraft: (value: string) => void;
  onActivate: (ref: CellRef) => void;
  onCellKey: (e: ReactKeyboardEvent<HTMLElement>, ref: CellRef) => void;
  onEditKey: (e: ReactKeyboardEvent<HTMLInputElement>, ref: CellRef) => void;
  onBlurCommit: (ref: CellRef) => void;
  onDelete: () => void;
}

/** One row. Split out so a re-render of the grid does not re-render 56 rows of inputs. */
function GridTr({
  row,
  r,
  columns,
  dirty,
  active,
  firstStop,
  editing,
  draft,
  error,
  onDraft,
  onActivate,
  onCellKey,
  onEditKey,
  onBlurCommit,
  onDelete,
}: GridTrProps) {
  const { t } = useTranslation();
  const cls = ['mgr-grid-row', row.deleted ? 'gone' : '', row.added ? 'new' : '', dirty ? 'dirty' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <tr className={cls} data-testid={`mgr-grid-row-${row.id}`} style={{ height: `${ROW_H}px` }}>
      <th scope="row" className="mgr-grid-gutter">
        {r + 1}
      </th>
      {columns.map((col, c) => {
        const isEditing = editing?.r === r && editing.c === c;
        const isActive = active?.r === r && active.c === c;
        const value = cellOf(row, col.key);
        // The tint only; the sentence lives at the grid's foot, because a `<td>` here is one
        // fixed-height line and a message inside it would be clipped.
        const cellErr = error && error.rowId === row.id && error.key === col.key;
        return (
          <td
            key={col.key}
            className={[
              col.kind === 'number' ? 'num' : '',
              col.editable ? '' : 'ro',
              cellErr ? 'bad' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid={`mgr-grid-cell-${row.id}-${col.key}`}
          >
            {isEditing ? (
              <input
                className="mgr-grid-input"
                autoFocus
                data-testid={`mgr-grid-input-${row.id}-${col.key}`}
                type={col.kind === 'number' ? 'number' : col.kind === 'date' ? 'date' : 'text'}
                value={draft}
                aria-label={col.label}
                onChange={(e) => onDraft(e.target.value)}
                onKeyDown={(e) => onEditKey(e, { r, c })}
                onBlur={() => onBlurCommit({ r, c })}
              />
            ) : (
              <span
                className="mgr-grid-cell"
                data-cell={`${r}-${c}`}
                role="button"
                /* Exactly one cell of the rendered slice is tabbable: the active one, or —
                   when the active cell has been scrolled out of the window — the first cell
                   of the first rendered row, so Tab never dead-ends into the grid. */
                tabIndex={isActive || (firstStop === r && c === 0) ? 0 : -1}
                aria-label={`${col.label}: ${value === null ? '' : String(value)}`}
                onClick={() => onActivate({ r, c })}
                onFocus={() => undefined}
                onKeyDown={(e) => onCellKey(e, { r, c })}
              >
                {value === null ? '' : String(value)}
              </span>
            )}
          </td>
        );
      })}
      <td className="mgr-grid-gutter">
        <button
          type="button"
          className="mgr-grid-del"
          data-testid={`mgr-grid-del-${row.id}`}
          aria-pressed={row.deleted}
          title={row.deleted ? t('manager.grid.restore') : t('manager.grid.del')}
          onClick={onDelete}
        >
          {row.deleted ? t('manager.grid.restore') : t('manager.grid.del')}
        </button>
      </td>
    </tr>
  );
}

export default RowsGrid;
